import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableQuestionCard } from '../../question/components/SortableQuestionCard';
import { AddQuestionButton } from '../../question/components/AddQuestionButton';
import { QuestionListSkeleton } from '../../question/components/QuestionSkeleton';
import { useTemplateQuestions } from '../../question/hooks/useQuestions';
import { useTemplateQuestionReorder } from '../../question/hooks/useQuestionReorder';
import { useProgressiveLoading } from '../../question/hooks/useProgressiveLoading';
import { CreateQuestionDTO, UpdateQuestionDTO, QuestionDTO } from '../../question/dtos';
import { useSurveyTemplate } from '../hooks/useSurveyTemplates';

interface TemplateBuilderParams extends Record<string, string | undefined> {
  tenantId: string;
  templateId: string;
}

export const TemplateBuilder: React.FC = () => {
  const { tenantId, templateId } = useParams<TemplateBuilderParams>();
  const navigate = useNavigate();
  const [previewMode, setPreviewMode] = useState(false);

  // Fetch template details
  const {
    data: templateData,
    isLoading: templateLoading,
    error: templateError,
  } = useSurveyTemplate(tenantId!, templateId!);

  // Note: Now using template-specific question hooks, no need for virtual survey ID

  // Fetch questions data for template
  const {
    data: questions = [],
    isLoading,
    error,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    isUpdating,
  } = useTemplateQuestions({
    tenantId: tenantId!,
    templateId: templateId!,
  });

  // Drag & drop reordering
  const { onDragEnd, isReordering, sortedQuestions } = useTemplateQuestionReorder({
    tenantId: tenantId!,
    templateId: templateId!,
    questions,
  });

  // Progressive loading for large question lists
  const {
    visibleItems: visibleQuestions,
    hasMore,
    isLoadingMore,
    loadedCount,
    totalCount,
    loadMore,
    progressPercentage,
  } = useProgressiveLoading({
    items: sortedQuestions,
    initialBatchSize: 10,
    incrementSize: 5,
    autoLoadThreshold: 3,
    enabled: sortedQuestions.length > 15,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleBack = () => {
    navigate(`/app/manage/universities/${tenantId}/survey-templates`);
  };

  const handleCreateQuestion = useCallback(
    async (data: CreateQuestionDTO) => {
      const template = templateData?.data;
      if (!template) {
        throw new Error('Template data not available');
      }

      const enrichedData: CreateQuestionDTO = {
        ...data,
        tenantId: template.tenantId,
        // For templates, we don't need subjectId as templates are tenant-scoped
      };

      await createQuestion.mutateAsync(enrichedData);
    },
    [createQuestion, templateData]
  );

  const handleUpdateQuestion = useCallback(
    async (questionId: string, data: UpdateQuestionDTO) => {
      await updateQuestion.mutateAsync({ questionId, data });
    },
    [updateQuestion]
  );

  const handleDeleteQuestion = useCallback(
    async (questionId: string) => {
      await deleteQuestion.mutateAsync(questionId);
    },
    [deleteQuestion]
  );

  // Combined loading and error states
  const isLoadingData = templateLoading || isLoading;
  const hasError = templateError || error;

  const renderLoadingSkeleton = () => (
    <QuestionListSkeleton count={5} variant="card" showDragHandle={true} staggered={true} />
  );

  const renderEmptyState = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        backgroundColor: 'grey.50',
        border: '2px dashed',
        borderColor: 'grey.300',
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Questions Yet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start building your template by adding your first question
      </Typography>
      <AddQuestionButton
        templateId={templateId}
        onAdd={handleCreateQuestion}
        size="large"
        variant="inline"
        disabled={!templateData?.data}
      />
    </Paper>
  );

  const renderErrorState = () => (
    <Alert severity="error" sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Failed to load data
      </Typography>
      <Typography variant="body2">
        {templateError?.message ||
          error?.message ||
          'An unexpected error occurred. Please try refreshing the page.'}
      </Typography>
    </Alert>
  );

  const renderQuestionsList = () => {
    if (sortedQuestions.length === 0) {
      return renderEmptyState();
    }

    return (
      <Box sx={{ overflow: 'visible', pl: 2 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={onDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={visibleQuestions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box
              sx={{
                borderRadius: 1,
                transition: 'background-color 0.2s ease',
                minHeight: '50px',
                overflow: 'visible',
              }}
            >
              <Stack spacing={3}>
                {/* Progress indicator for large lists */}
                {totalCount > 15 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {loadedCount} of {totalCount} questions
                    </Typography>
                    <Box sx={{ flex: 1, height: 4, backgroundColor: 'grey.200', borderRadius: 2 }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${progressPercentage}%`,
                          backgroundColor: 'primary.main',
                          borderRadius: 2,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Questions list */}
                {visibleQuestions.map((question: QuestionDTO, index: number) => (
                  <SortableQuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                    onUpdate={handleUpdateQuestion}
                    onDelete={handleDeleteQuestion}
                    isUpdating={isUpdating || isReordering}
                    isDragDisabled={isUpdating || isReordering}
                  />
                ))}

                {/* Load more button */}
                {hasMore && !isLoadingMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={loadMore}
                      disabled={isUpdating || isReordering}
                    >
                      Load More Questions ({totalCount - loadedCount} remaining)
                    </Button>
                  </Box>
                )}

                {/* Single add button at the bottom */}
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <AddQuestionButton
                    templateId={templateId}
                    onAdd={handleCreateQuestion}
                    disabled={isUpdating || isReordering || !templateData?.data}
                    size="large"
                    variant="inline"
                  />
                </Box>
              </Stack>
            </Box>
          </SortableContext>
        </DndContext>
      </Box>
    );
  };

  if (!tenantId || !templateId) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">Tenant ID and Template ID are required to build questions</Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Header Bar */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Template Builder - {templateData?.data?.title || 'Loading...'}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={
                totalCount > 15
                  ? `${loadedCount}/${totalCount} Questions`
                  : `${sortedQuestions.length} Questions`
              }
              size="small"
              color={sortedQuestions.length > 0 ? 'primary' : 'default'}
              variant="outlined"
            />

            {isReordering && (
              <Chip label="Reordering..." size="small" color="warning" variant="outlined" />
            )}

            {isLoadingMore && (
              <Chip label="Loading more..." size="small" color="info" variant="outlined" />
            )}

            <Button
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewMode(!previewMode)}
              variant={previewMode ? 'contained' : 'outlined'}
              size="small"
              disabled={isReordering}
            >
              {previewMode ? 'Edit' : 'Preview'}
            </Button>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleBack}
              size="small"
              disabled={isReordering}
            >
              Save & Close
            </Button>
          </Stack>
        </Toolbar>
        <Divider />
      </AppBar>

      {/* Main Content */}
      <Container maxWidth={false} disableGutters sx={{ overflow: 'visible' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Question Template Builder
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build your template by adding and configuring questions. Use the drag handle (⋮⋮) on the
            left to reorder questions.
          </Typography>
        </Box>

        {hasError && renderErrorState()}

        {isLoadingData ? (
          renderLoadingSkeleton()
        ) : (
          <>
            {previewMode ? (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Template Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  This is how your template will appear to lecturers creating surveys
                </Typography>

                <Stack spacing={3}>
                  {sortedQuestions.map((question: QuestionDTO, index: number) => (
                    <Box key={question.id}>
                      <Typography variant="h6" gutterBottom>
                        {index + 1}. {question.title.en}
                        {question.isRequired && <span style={{ color: 'red' }}> *</span>}
                      </Typography>

                      {question.description?.en && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {question.description.en}
                        </Typography>
                      )}

                      <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          [{question.type} question input would appear here]
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : (
              renderQuestionsList()
            )}
          </>
        )}
      </Container>
    </>
  );
};
